/**
 * 회원가입 페이지
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Card } from '../components/ui/Card';
import { toast } from '../components/ui/Toast';
import { validateEmail, validatePassword, validatePasswordConfirm } from '../utils/validation';
import PasswordStrengthMeter from '../components/ui/PasswordStrengthMeter';
import GoogleLoginButton from '../components/auth/GoogleLoginButton';

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const register = useAuthStore((state) => state.register);
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    password?: string;
    confirmPassword?: string;
  }>({});

  const handleNameBlur = () => {
    if (!name) setErrors((p) => ({ ...p, name: '이름을 입력해주세요.' }));
  };

  const handleEmailBlur = () => {
    const result = validateEmail(email);
    if (!result.ok) setErrors((p) => ({ ...p, email: result.message }));
  };

  const handlePasswordBlur = () => {
    const result = validatePassword(password);
    if (!result.ok) setErrors((p) => ({ ...p, password: result.message }));
  };

  const handleConfirmPasswordBlur = () => {
    if (!confirmPassword) {
      setErrors((p) => ({ ...p, confirmPassword: '비밀번호를 다시 입력해주세요.' }));
    } else {
      const result = validatePasswordConfirm(password, confirmPassword);
      if (!result.ok) setErrors((p) => ({ ...p, confirmPassword: result.message }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 유효성 검증
    const nextErrors: typeof errors = {};
    if (!name) nextErrors.name = '이름을 입력해주세요.';
    const emailResult = validateEmail(email);
    if (!emailResult.ok) nextErrors.email = emailResult.message;
    const pwResult = validatePassword(password);
    if (!pwResult.ok) nextErrors.password = pwResult.message;
    else {
      const pcResult = validatePasswordConfirm(password, confirmPassword);
      if (!pcResult.ok) nextErrors.confirmPassword = pcResult.message;
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      toast({
        title: '입력 오류',
        description: '입력 내용을 확인해주세요.',
        variant: 'destructive',
      });
      return;
    }

    setErrors({});

    setIsLoading(true);

    try {
      await register(email, password, name);
      
      toast({
        title: '회원가입 성공',
        description: '환영합니다! 로그인되었습니다.',
        variant: 'default',
      });
      
      // 신규 가입자는 에디터 투어로 바로 진입
      navigate('/albums/new?tour=1');
    } catch (error: any) {
      console.error('회원가입 에러:', error);
      
      toast({
        title: '회원가입 실패',
        description: error.response?.data?.message || '회원가입 중 오류가 발생했습니다.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-dvh flex items-center justify-center bg-gradient-to-br from-primary-50 via-secondary-50/40 to-white dark:from-vinyl dark:via-primary-950 dark:to-vinyl px-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
      <Card className="w-full max-w-md p-8 shadow-2xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 dark:text-slate-200 mb-2">회원가입</h1>
          <p className="text-gray-600 dark:text-slate-400">새 계정을 만들어 시작하세요</p>
        </div>

        <form onSubmit={handleSubmit} aria-label="회원가입 폼" className="space-y-5">
          <div>
            <Label htmlFor="name" required className="block text-gray-700 dark:text-slate-300 mb-2">
              이름
            </Label>
            <Input
              id="name"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) setErrors((p) => ({ ...p, name: undefined }));
              }}
              onBlur={handleNameBlur}
              placeholder="홍길동"
              className="w-full"
              disabled={isLoading}
              autoComplete="name"
              required
              error={errors.name}
            />
          </div>

          <div>
            <Label htmlFor="email" required className="block text-gray-700 dark:text-slate-300 mb-2">
              이메일
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors((p) => ({ ...p, email: undefined }));
              }}
              onBlur={handleEmailBlur}
              placeholder="your@email.com"
              className="w-full"
              disabled={isLoading}
              autoComplete="email"
              required
              error={errors.email}
            />
          </div>

          <div>
            <Label htmlFor="password" required className="block text-gray-700 dark:text-slate-300 mb-2">
              비밀번호
            </Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password) setErrors((p) => ({ ...p, password: undefined }));
              }}
              onBlur={handlePasswordBlur}
              placeholder="최소 6자"
              className="w-full"
              disabled={isLoading}
              autoComplete="new-password"
              required
              minLength={6}
              error={errors.password}
            />
            <PasswordStrengthMeter password={password} />
          </div>

          <div>
            <Label htmlFor="confirmPassword" required className="block text-gray-700 dark:text-slate-300 mb-2">
              비밀번호 확인
            </Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (errors.confirmPassword) setErrors((p) => ({ ...p, confirmPassword: undefined }));
              }}
              onBlur={handleConfirmPasswordBlur}
              placeholder="비밀번호 재입력"
              className="w-full"
              disabled={isLoading}
              autoComplete="new-password"
              required
              minLength={6}
              error={errors.confirmPassword}
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? '가입 중...' : '회원가입'}
          </Button>
        </form>

        {/* 구분선 */}
        <div className="mt-6 mb-6 flex items-center">
          <div className="flex-1 border-t border-gray-300 dark:border-slate-600"></div>
          <span className="px-4 text-sm text-gray-500 dark:text-slate-400">또는</span>
          <div className="flex-1 border-t border-gray-300 dark:border-slate-600"></div>
        </div>

        {/* Google 회원가입 */}
        <GoogleLoginButton text="Google로 계속하기" disabled={isLoading} />

        <div className="mt-6 text-center text-sm text-gray-600 dark:text-slate-400">
          이미 계정이 있으신가요?{' '}
          <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
            로그인
          </Link>
        </div>

        <div className="mt-4 text-center">
          <Link to="/" className="text-sm text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200">
            ← 로그인 없이 사용하기
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default SignupPage;

